import ActionStatus from '@sunbit-dev/common/enums/ActionStatus';
import PaymentMethodCategory from '@sunbit-dev/common/enums/cardApply/PaymentMethodCategory';
import PaymentMethodTypes from '@sunbit-dev/common/enums/PaymentMethodTypes';
import { isEmpty } from '@sunbit-dev/common/helpers/isEmpty';
import ApplicationError from '@sunbit-dev/common/models/ApplicationError';
import INewPaymentMethodSubmit from '@sunbit-dev/common/models/cardApply/INewPaymentMethodSubmit';
import IPaymentDetailsSubmit from '@sunbit-dev/common/models/cardApply/IPaymentDetailsSubmit';
import { replace } from 'connected-react-router';
import { ERROR_CODES } from '../errors/errors';
import CommonThunk from '../models/CommonThunk';
import sunbitApi from '../services/sunbitApi';
import IAddress from '@sunbit-dev/common/models/IAddress';
import URLS from '../constants/urls';
import { sampling } from '@sunbit-dev/common/services/sampling';
import { getIovationBlackbox } from '../helpers/iovation/iovation';
import { datadogRum } from '@sunbit-dev/common/services/datadog';

export enum ActionTypes {
  SubmitExistingPaymentMethod = 'SubmitExistingPaymentMethod',
  SubmitNewPaymentMethod = 'SubmitNewPaymentMethod',
  SubmitNewPaymentMethodRCC = 'SubmitNewPaymentMethodRCC',
  SubmitDueDate = 'SubmitDueDate',
  SubmitNewApplication = 'SubmitNewApplication',
}

const submitExistingPaymentMethodPending = (paymentDetailsSubmit: IPaymentDetailsSubmit) => ({
  type: ActionTypes.SubmitExistingPaymentMethod,
  status: ActionStatus.Pending,
  paymentDetailsSubmit,
});

const submitExistingPaymentMethodSuccess = () => ({
  type: ActionTypes.SubmitExistingPaymentMethod,
  status: ActionStatus.Success,
});

const submitExistingPaymentMethodFailure = (error: any) => ({
  type: ActionTypes.SubmitExistingPaymentMethod,
  status: ActionStatus.Failure,
  error,
});

const sampleDevice = () => {
  try {
    const blackbox = getIovationBlackbox();
    return sampling.event('save_iovation_bb', { iovation: { blackbox } });
  } catch (error) {
    datadogRum.addError('Sample Device', { error });
  }
};

export const submitExistingPaymentMethodThunk = (paymentDetailsSubmit: IPaymentDetailsSubmit): CommonThunk => {
  return async (dispatch) => {
    dispatch(submitExistingPaymentMethodPending(paymentDetailsSubmit));
    try {
      if (!paymentDetailsSubmit.dueDate) {
        throw new ApplicationError({ code: ERROR_CODES.dueDateIsMissing, message: 'Due date is missing' });
      }
      if (isEmpty(paymentDetailsSubmit.existingPaymentMethod)) {
        throw new ApplicationError({
          code: ERROR_CODES.existingPaymentMethodSelectionMissing,
          message: 'Missing existing payment method',
        });
      }

      const sampleDevicePromise = sampleDevice();
      await sunbitApi.assignExistingPaymentMethod(paymentDetailsSubmit.existingPaymentMethod);
      await sunbitApi.setDueDate(paymentDetailsSubmit.dueDate);
      dispatch(submitExistingPaymentMethodSuccess());
      await sampleDevicePromise;
      await sunbitApi.applyForCard();
      dispatch(replace(URLS.applicationThanks));
    } catch (error) {
      dispatch(submitExistingPaymentMethodFailure(error as ApplicationError));
    }
  };
};

const submitNewPaymentMethodPending = (paymentDetailsSubmit: IPaymentDetailsSubmit) => ({
  type: ActionTypes.SubmitNewPaymentMethod,
  status: ActionStatus.Pending,
  paymentDetailsSubmit,
});

const submitNewPaymentMethodSuccess = () => ({
  type: ActionTypes.SubmitNewPaymentMethod,
  status: ActionStatus.Success,
});

const submitNewPaymentMethodFailure = (error: any) => ({
  type: ActionTypes.SubmitNewPaymentMethod,
  status: ActionStatus.Failure,
  error,
});

const submitNewPaymentMethodFailureRCC = (error: any) => ({
  type: ActionTypes.SubmitNewPaymentMethodRCC,
  status: ActionStatus.Failure,
  error,
});

export const submitNewPaymentMethodThunk = (
  paymentDetailsSubmit: IPaymentDetailsSubmit,
  applicationId: number,
  RCC?: boolean
): CommonThunk => {
  return async (dispatch) => {
    dispatch(submitNewPaymentMethodPending(paymentDetailsSubmit));
    try {
      if (!paymentDetailsSubmit.dueDate) {
        throw new Error('Due date is missing');
      }
      let newPaymentMethod: INewPaymentMethodSubmit;
      if (
        paymentDetailsSubmit.paymentMethodCategory === PaymentMethodCategory.Debit &&
        !isEmpty(paymentDetailsSubmit.debitCardDetails)
      ) {
        newPaymentMethod = {
          paymentMethodType: PaymentMethodTypes.DEBIT,
          debitCardDetails: paymentDetailsSubmit.debitCardDetails,
        };
      } else if (
        paymentDetailsSubmit.paymentMethodCategory === PaymentMethodCategory.RCC &&
        !isEmpty(paymentDetailsSubmit.rccDetails)
      ) {
        newPaymentMethod = {
          paymentMethodType: PaymentMethodTypes.RCC,
          rccDetails: paymentDetailsSubmit.rccDetails,
        };
      } else if (paymentDetailsSubmit.paymentMethodCategory === PaymentMethodCategory.MAIL) {
        newPaymentMethod = {
          paymentMethodType: PaymentMethodTypes.MAIL,
        };
      } else {
        throw new Error('Data is missing to complete the new payment method request');
      }

      const sampleDevicePromise = sampleDevice();
      await sunbitApi.assignNewPaymentMethod(newPaymentMethod, applicationId);
      await sunbitApi.setDueDate(paymentDetailsSubmit.dueDate);
      dispatch(submitNewPaymentMethodSuccess());
      await sampleDevicePromise;
      await sunbitApi.applyForCard();
      dispatch(replace(URLS.applicationThanks));
    } catch (error) {
      if (RCC) {
        dispatch(submitNewPaymentMethodFailureRCC(error));
      } else {
        dispatch(submitNewPaymentMethodFailure(error));
      }
    }
  };
};

const submitNewApplicationPending = (address: IAddress) => ({
  type: ActionTypes.SubmitNewApplication,
  status: ActionStatus.Pending,
  address,
});

const submitNewApplicationSuccess = () => ({
  type: ActionTypes.SubmitNewApplication,
  status: ActionStatus.Success,
});

const submitNewApplicationFailure = (error: any) => ({
  type: ActionTypes.SubmitNewApplication,
  status: ActionStatus.Failure,
  error,
});

export type Actions =
  | ReturnType<typeof submitNewApplicationPending>
  | ReturnType<typeof submitNewApplicationSuccess>
  | ReturnType<typeof submitNewApplicationFailure>
  | ReturnType<typeof submitExistingPaymentMethodPending>
  | ReturnType<typeof submitExistingPaymentMethodSuccess>
  | ReturnType<typeof submitExistingPaymentMethodFailure>
  | ReturnType<typeof submitNewPaymentMethodPending>
  | ReturnType<typeof submitNewPaymentMethodSuccess>
  | ReturnType<typeof submitNewPaymentMethodFailure>;
